import type { ReceiptLoyaltyBlock, ReceiptReviewBlock } from '@/lib/printCustomerReceipt'

/**
 * One marketing QR per receipt by default. Loyalty wins — unless the tenant
 * says otherwise (receipt_qr_with_loyalty), in which case both print.
 *
 * Two 30mm QRs plus their headline blocks, on top of the coupon stub and the
 * integrated tax block, push an 80mm slip past 25cm. The paper cost is the
 * lesser problem: a guest handed two codes scans neither, so a second QR makes
 * the first one worse.
 *
 * Loyalty takes precedence whenever it is actually printing a capture QR,
 * because the two asks are not worth the same. A captured phone number is what
 * makes every future campaign, WhatsApp alert and recovery possible — the
 * review link can then be *sent* to a guest we now own. A rating from a guest
 * we can't identify is worth having, but it's worth less, and it's the one we
 * can afford to defer.
 *
 * The escalation falls out of that nicely: a new guest gets asked for their
 * identity, and a guest we already know gets asked for their opinion.
 *
 *   loyalty 'unsaved'                     → loyalty keeps the QR; review → text
 *   loyalty 'saved' (prints no QR anyway) → review gets the QR   ← the sweet spot
 *   loyalty 'unsaved_no_url' / absent     → review gets the QR
 *
 * Pure function, no I/O — the caller fetches both blocks concurrently and then
 * passes them through here before printing.
 */
export function resolveReceiptQrSlots(
  loyalty: ReceiptLoyaltyBlock | null | undefined,
  review: ReceiptReviewBlock | null | undefined,
): {
  loyalty: ReceiptLoyaltyBlock | null
  review: ReceiptReviewBlock | null
} {
  const loyaltyOut = loyalty ?? null
  const reviewOut = review ?? null

  // Loyalty is printing a scannable capture QR. By default the review ask is
  // dropped — two codes on one slip scan worse and cost paper — but that is
  // the shop's trade-off to make, not ours: when the tenant has switched on
  // receipt_qr_with_loyalty (carried here as allowWithLoyalty), both print.
  if (loyaltyOut?.state === 'unsaved' && reviewOut && !reviewOut.allowWithLoyalty) {
    return { loyalty: loyaltyOut, review: null }
  }

  return { loyalty: loyaltyOut, review: reviewOut }
}
